import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router';
import { Button, Dialog } from '@awj/ui';

import { useI18n } from '~/i18n/index.tsx';

/**
 * Asks before unsaved edits are thrown away — by a link in the rail, the browser's back button,
 * or a reload.
 *
 * Render `dialog` somewhere in the editor. Call `release()` as soon as a save succeeds, and before
 * any navigation the editor makes on purpose (after a save, or "Discard"): until React renders the
 * new saved copy the form still reads as dirty, and a navigation in that gap — the editor's own, or
 * a click on the rail straight after Save — would be asked whether to throw away what was just saved.
 */
export function useUnsavedChanges(dirty: boolean) {
  const { t } = useI18n();

  // Read when a navigation happens, not when the blocker was last rendered. `release()` clears it
  // at once; the next render sets it again from the form, so an edit made after that still counts.
  const dirtyNow = useRef(dirty);
  dirtyNow.current = dirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyNow.current &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  // A reload or a closed tab never reaches the router; the browser's own prompt is all there is.
  useEffect(() => {
    if (!dirty) {
      return;
    }

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const dialog = (
    <Dialog
      open={blocker.state === 'blocked'}
      size="sm"
      onClose={() => blocker.reset?.()}
      title={t('content.leaveTitle')}
      description={t('editor.leaveHint')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => blocker.reset?.()}>
            {t('content.keepEditing')}
          </Button>
          <Button type="button" variant="danger" onClick={() => blocker.proceed?.()}>
            {t('content.discard')}
          </Button>
        </>
      }
    >
      {null}
    </Dialog>
  );

  return {
    dialog,
    release: () => {
      dirtyNow.current = false;
    },
  };
}
